import 'bootstrap/dist/css/bootstrap.min.css';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import React, { useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import { baseurl } from '../utility/helper';

// ─── Utility Helpers ──────────────────────────────────────────────────────────

const formatMonthYear = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  return date.toLocaleDateString('en-GB', { month: '2-digit', year: 'numeric' });
};

const buildAddress = (row) =>
  [row.present_address, row.present_city, row.present_state, row.present_pincode]
    .filter((v) => v && v !== '')
    .join(', ') || '-';

// ── Constants ──────────────────────────────────────────────────────────────
const PASSPORT_W_PX   = 130;   // passport photo width in px  (~35mm)
const PASSPORT_H_PX   = 170;   // passport photo height in px (~45mm)
const CELL_PADDING    = 10;    // px padding around the passport box inside the cell
const COL_PHOTO_UNITS = 20;    // Excel column width units
const COL_PHOTO_PX    = Math.round(COL_PHOTO_UNITS * 7 + 5); // Excel units → px
const ROW_HEIGHT_PT   = 135;   // Excel row height in points
const ROW_HEIGHT_PX   = ROW_HEIGHT_PT * (4 / 3); // pt → px (96 DPI)

const fetchImageAsBase64 = (url) =>
  new Promise((resolve) => {
    try {
      const img = new Image();
      img.crossOrigin = 'anonymous';

      img.onload = () => {
        try {
          const SCALE = 2;
          const cw    = PASSPORT_W_PX * SCALE; // 264 px
          const ch    = PASSPORT_H_PX * SCALE; // 340 px

          const canvas = document.createElement('canvas');
          canvas.width  = cw;
          canvas.height = ch;
          const ctx = canvas.getContext('2d');

          // White background (covers transparent PNGs + fills padding)
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, cw, ch);

          // ── CONTAIN-fit: show the FULL photo, no cropping ──
          // Math.min (not Math.max) ensures the entire image fits inside
          // the frame — the whole head/face is always visible.
          const srcW  = img.naturalWidth  || img.width;
          const srcH  = img.naturalHeight || img.height;
          const ratio = Math.min(cw / srcW, ch / srcH);
          const drawW = srcW * ratio;
          const drawH = srcH * ratio;
          const dx    = (cw - drawW) / 2;
          const dy    = (ch - drawH) / 2;

          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          ctx.drawImage(img, dx, dy, drawW, drawH);

          const dataUrl = canvas.toDataURL('image/png');
          resolve({ base64: dataUrl.split(',')[1], extension: 'png' });
        } catch {
          resolve(null);
        }
      };

      img.onerror = () => resolve(null);
      img.src = url;
    } catch {
      resolve(null);
    }
  });
// ─── Excel Export with Passport-size Images ───────────────────────────────────
const formatToIST = (value) => {
  if (!value) return '-';

  const date = new Date(value);

  return date.toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',   // 🇮🇳 India timezone
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',          // 12-hour format (AM/PM)
  });
};

// ── Helper: get natural width/height of an image from base64 ───────────────
function getImageDimensions(base64) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload  = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = reject;
    img.src = `data:image/png;base64,${base64}`; // browser sniffs real format from bytes
  });
}

const exportToExcelWithImages = async (rows, filename = 'CardList') => {
  Swal.fire({
    title: 'Preparing Excel...',
    text: 'Embedding passport-size photos, please wait.',
    allowOutsideClick: false,
    didOpen: () => Swal.showLoading(),
  });

  try {
    const workbook = new ExcelJS.Workbook();
    const sheet    = workbook.addWorksheet('Card List');

    sheet.columns = [
      { header: 'Sr.No',          key: 'sr',            width: 8           },
      { header: 'Photo',          key: 'photo',         width: COL_PHOTO_UNITS },
      { header: 'Membership No',  key: 'membership_no', width: 18          },
      { header: 'Name',           key: 'name',          width: 25          },
      { header: 'Mobile',         key: 'mobileno',      width: 25          },
      { header: 'Address',        key: 'address',       width: 38          },
      { header: 'Valid Till',     key: 'membership_exp_date', width: 14    },
    ];

    const headerRow = sheet.getRow(1);
    headerRow.height = 30;
    headerRow.eachCell((cell) => {
      cell.font      = { bold: true, color: { argb: 'FFFFFFFF' }, name: 'Arial', size: 11 };
      cell.fill      = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1D4ED8' } };
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
      cell.border    = {
        top: { style: 'thin' }, bottom: { style: 'thin' },
        left: { style: 'thin' }, right: { style: 'thin' },
      };
    });

    for (let i = 0; i < rows.length; i++) {
      const row    = rows[i];
      const excelR = i + 2;

      const dataRow = sheet.addRow({
        sr:            i + 1,
        photo:         '',
        membership_no: row.membership_no || '-',
        name: `${row.first_name || '-'} ${row.last_name || '-'}`.trim(),
        mobileno: row.mobile_no || '-',
        address:       buildAddress(row),
        membership_exp_date: row.memberstatus_id === '1' ? 'Life' : formatMonthYear(row.membership_exp_date),
      });

      dataRow.height = ROW_HEIGHT_PT;

      dataRow.eachCell({ includeEmpty: true }, (cell) => {
        cell.font      = { name: 'Arial', size: 10 };
        cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
        cell.fill = i % 2 === 0
          ? { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFFFF' } }
          : { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF0F4FF' } };
        cell.border = {
          top:    { style: 'thin', color: { argb: 'FFE5E7EB' } },
          bottom: { style: 'thin', color: { argb: 'FFE5E7EB' } },
          left:   { style: 'thin', color: { argb: 'FFE5E7EB' } },
          right:  { style: 'thin', color: { argb: 'FFE5E7EB' } },
        };
      });

      // ── Embed photo — "object-fit: contain" inside the PASSPORT_W_PX x PASSPORT_H_PX box ──
      if (row.photo_url) {
        const imgData = await fetchImageAsBase64(row.photo_url);
        if (imgData) {
          const imageId  = workbook.addImage({ base64: imgData.base64, extension: imgData.extension });
          const rowIndex = excelR - 1;

          // 1. Real image dimensions
          let naturalW = PASSPORT_W_PX;
          let naturalH = PASSPORT_H_PX;
          try {
            const dims = await getImageDimensions(imgData.base64);
            naturalW = dims.width;
            naturalH = dims.height;
          } catch {
            // fall back to passport box size if decoding fails
          }

          // 2. CONTAIN: scale so the image fits fully within the passport box,
          //    never exceeding either dimension, aspect ratio preserved.
          const scale = Math.min(PASSPORT_W_PX / naturalW, PASSPORT_H_PX / naturalH, 1);
          const drawW = Math.round(naturalW * scale);
          const drawH = Math.round(naturalH * scale);

          // 3. Center the scaled image inside the passport box, and center
          //    the passport box inside the cell.
          const boxOffsetX = (COL_PHOTO_PX - PASSPORT_W_PX) / 2;
          const boxOffsetY = (ROW_HEIGHT_PX - PASSPORT_H_PX) / 2;
          const imgOffsetX = boxOffsetX + (PASSPORT_W_PX - drawW) / 2;
          const imgOffsetY = boxOffsetY + (PASSPORT_H_PX - drawH) / 2;

          sheet.addImage(imageId, {
            tl: {
              col: 1 + imgOffsetX / COL_PHOTO_PX,
              row: rowIndex + imgOffsetY / ROW_HEIGHT_PX,
            },
            ext: {
              width:  drawW,
              height: drawH,
            },
            editAs: 'oneCell', // pin to cell so it doesn't drift if row/col sizes change
          });
        }
      }
    }

    sheet.views = [{ state: 'frozen', ySplit: 1 }];

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer], { type: 'application/octet-stream' }), `${filename}.xlsx`);

    Swal.fire({ icon: 'success', title: 'Exported!', text: `${filename}.xlsx downloaded.`, timer: 2000, showConfirmButton: false });
  } catch (err) {
    console.error(err);
    Swal.fire('Error', 'Failed to export Excel.', 'error');
  }
};
// ─── Reusable Table Component ─────────────────────────────────────────────────

const ITEMS_PER_PAGE_OPTIONS = [10, 25, 50, 100, 500, 'All'];


const CardTable = ({ data, isCompleted, roleid, onStatusChange, filename }) => {
  const [search,   setSearch]   = useState('');
  const [sortKey,  setSortKey]  = useState('');
  const [sortDir,  setSortDir]  = useState('asc');
  const [page,     setPage]     = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Filter
  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    if (!q) return data;
    return data.filter((row) =>
      [row.chapter_id, row.membership_no, row.payment_no,
       row.first_name, row.last_name, row.cstatus, buildAddress(row)]
        .some((v) => String(v || '').toLowerCase().includes(q))
    );
  }, [data, search]);

  // Sort
  const sorted = useMemo(() => {
    if (!sortKey) return filtered;
    return [...filtered].sort((a, b) => {
      const av = String(a[sortKey] || '').toLowerCase();
      const bv = String(b[sortKey] || '').toLowerCase();
      return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    });
  }, [filtered, sortKey, sortDir]);

  const totalPages = pageSize === Infinity ? 1 : Math.max(1, Math.ceil(sorted.length / pageSize));
  const paginated = pageSize === Infinity
    ? sorted
    : sorted.slice((page - 1) * pageSize, page * pageSize);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('asc'); }
    setPage(1);
  };

  const SortIcon = ({ col }) =>
    sortKey !== col
      ? <span style={{ opacity: 0.35, marginLeft: 4, fontSize: 11 }}>⇅</span>
      : <span style={{ marginLeft: 4, fontSize: 11 }}>{sortDir === 'asc' ? '↑' : '↓'}</span>;

  const thStyle = {
    background: '#DBE6FF', color: '#000', padding: '10px 12px',
    textAlign: 'center', whiteSpace: 'nowrap', cursor: 'pointer',
    fontSize: 13, borderRight: '1px solid #2563EB', userSelect: 'none',
  };
  const tdStyle = {
    padding: '8px 10px', textAlign: 'center', verticalAlign: 'middle',
    fontSize: 13, borderBottom: '1px solid #E5E7EB',
    border: '1px solid black', color: 'black'
  };

  const StatusBadge = ({ status }) => {
    const map = {
      approved:  { bg: '#DBEAFE', color: '#1D4ED8' },
      rejected:  { bg: '#FEE2E2', color: '#B91C1C' },
      delivered: { bg: '#D1FAE5', color: '#065F46' },
      pending:   { bg: '#FEF9C3', color: '#854D0E' },
    };
    const s = map[status?.toLowerCase()] || { bg: '#F3F4F6', color: '#374151' };
    return (
      <span style={{
        background: s.bg, color: s.color, padding: '3px 10px',
        borderRadius: 12, fontSize: 12, fontWeight: 600, textTransform: 'capitalize',
      }}>{status || '-'}</span>
    );
  };

  return (
    <div>
      {/* Toolbar */}
      <div className="d-flex flex-wrap align-items-center justify-content-between mb-3 gap-2">
        <div className="d-flex align-items-center gap-2">
          <span style={{ fontSize: 13 }}>Show</span>
          <select
            className="form-select form-select-sm"
            style={{ width: 80 }}
            value={pageSize}
            onChange={(e) => {
              const val = e.target.value;
              setPageSize(val === 'All' ? Infinity : Number(val));
              setPage(1);
            }}
          >
            {ITEMS_PER_PAGE_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
          <span style={{ fontSize: 13 }}>entries</span>
        </div>

        <div className="d-flex align-items-center gap-2 flex-wrap">
          <button className="btn btn-sm btn-success" onClick={() => exportToExcelWithImages(data, filename)}>
            Excel with Photos
          </button>
          <input
            type="text"
            className="form-control form-control-sm"
            placeholder="Search..."
            style={{ width: 200 }}
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          />
        </div>
      </div>

      {/* Table */}
      <div style={{ overflowX: 'auto', borderRadius: 8, border: '1px solid #E5E7EB', boxShadow: '0 1px 6px rgba(0,0,0,0.06)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 950 }}>
          <thead>
            <tr>
              <th style={thStyle}>Sr.No</th>
              <th style={thStyle}>Membership No</th>
              <th style={thStyle}>Chapter ID</th>
              <th style={thStyle}>Name</th>
              <th style={thStyle}>Photo</th>
              <th style={thStyle}>Mobile</th>
              <th style={thStyle}>Email</th>
              {[
                { label: 'Address',        key: null            },
                { label: 'Request Date',   key: 'request_date'  },
                { label: 'Valid Till',     key: 'membership_exp_date'   },
              ].map(({ label, key }) => (
                <th key={label} style={thStyle} onClick={() => key && toggleSort(key)}>
                  {label}{key && <SortIcon col={key} />}
                </th>
              ))}
              <th style={{ ...thStyle, cursor: 'default' }}>
                {isCompleted ? 'Status' : 'Action'}
              </th>
            </tr>
          </thead>
          <tbody>
            {paginated.length === 0 ? (
              <tr>
                <td colSpan={8} style={{ ...tdStyle, padding: 40, textAlign: 'center' }}>
                  No records found.
                </td>
              </tr>
            ) : paginated.map((row, i) => {
              const globalIdx = (page - 1) * pageSize + i;

              return (
                <tr key={row.card_id || i}>
                  {/* 1. Sr.No */}
                  <td style={tdStyle}>{globalIdx + 1}</td>

                  {/* 2. Membership No */}
                  <td style={tdStyle}>
                    {row.membership_no ? (
                      <a
                        href={`/admin/EditMember?memberId=${row.member_id}&edit=false`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {row.membership_no}
                      </a>
                    ) : '-'}
                  </td>

                  {/* 3. Chapter ID */}
                  <td style={tdStyle}>{row.chapter_id || '-'}</td>

                  {/* 4. Name */}
                  <td style={tdStyle}>
                    {`${row.first_name || ''} ${row.last_name || ''}`.trim() || '-'}
                  </td>

                  {/* 5. Photo */}
                  <td style={tdStyle}>
                    {row.photo_url ? (
                      <img
                        src={row.photo_url}
                        alt="Photo"
                        style={{
                          width: 42,
                          height: 42,
                          borderRadius: '50%',
                          objectFit: 'contain',
                          border: '2px solid #E5E7EB',
                          cursor: 'pointer'
                        }}
                      />
                    ) : (
                      <div style={{
                        width: 42,
                        height: 42,
                        borderRadius: '50%',
                        background: '#6C757D',
                        color: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 700
                      }}>
                        {`${(row.first_name || '')[0] || ''}${(row.last_name || '')[0] || ''}`.toUpperCase() || '?'}
                      </div>
                    )}
                  </td>

                  {/* 6. Mobile */}
                  <td style={tdStyle}>{row.mobile_no || '-'}</td>

                  {/* 7. Email */}
                  <td style={tdStyle}>{row.email || '-'}</td>

                  {/* 8. Address */}
                  <td style={{ ...tdStyle, textAlign: 'left' }}>
                    {buildAddress(row)}
                  </td>
                  <td style={tdStyle}>{formatToIST(row.request_date)  || '-'}</td>
                  <td style={tdStyle}>
                    {row.memberstatus_id === '1'
                      ? "Life"
                      : formatMonthYear(row.membership_exp_date)}
                  </td>
                  {/* Action / Status */}
                  <td style={tdStyle}>
                    {roleid === 1 ? (
                      <select
                        className="form-select form-select-sm"
                        style={{ width: 145, margin: 'auto' }}
                        value={row.cstatus || ''}
                        onChange={(e) => onStatusChange(row.card_id, e.target.value, e)}
                      >
                        <option value="">-- Change Status --</option>
                        <option value="approved">Approved</option>
                        <option value="rejected">Rejected</option>
                        <option value="delivered">Delivered</option>
                      </select>
                    ) : (
                      <StatusBadge status={row.cstatus} />
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="d-flex align-items-center justify-content-between mt-3 flex-wrap gap-2">
        <span style={{ fontSize: 13, color: '#6B7280' }}>
          Showing {sorted.length === 0 ? 0 : (page - 1) * pageSize + 1}–{Math.min(page * pageSize, sorted.length)} of {sorted.length} entries
          {search && ` (filtered from ${data.length} total)`}
        </span>
        <div className="d-flex gap-1">
          <button className="btn btn-sm btn-outline-secondary" disabled={page === 1} onClick={() => setPage(1)}>«</button>
          <button className="btn btn-sm btn-outline-secondary" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>‹</button>
          {Array.from({ length: Math.min(5, totalPages) }, (_, idx) => {
            let p;
            if      (totalPages <= 5)         p = idx + 1;
            else if (page <= 3)               p = idx + 1;
            else if (page >= totalPages - 2)  p = totalPages - 4 + idx;
            else                              p = page - 2 + idx;
            return (
              <button
                key={p}
                className={`btn btn-sm ${page === p ? 'btn-primary' : 'btn-outline-secondary'}`}
                onClick={() => setPage(p)}
              >{p}</button>
            );
          })}
          <button className="btn btn-sm btn-outline-secondary" disabled={page === totalPages} onClick={() => setPage((p) => p + 1)}>›</button>
          <button className="btn btn-sm btn-outline-secondary" disabled={page === totalPages} onClick={() => setPage(totalPages)}>»</button>
        </div>
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const CardList = () => {
  const roleid = parseInt(sessionStorage.getItem('userroleId'));
  const [cardListData, setCardListData] = useState([]);
  const [loading,      setLoading]      = useState(true);
  const initialChapter = roleid === 2 ? sessionStorage.getItem('userchapterId') || '' : '';
  const [chapterId]    = useState(initialChapter);

  const fetchCardList = async () => {
    setLoading(true);
    try {
      const token = sessionStorage.getItem('token');
      let url = `${baseurl}/admin/CardList`;
      if (roleid !== 1) url += `?chapter_id=${chapterId}`;
      const res = await fetch(url, {
        method: 'GET',
        headers: { Authorization: `${token}`, 'Content-Type': 'application/json' },
      });
      if (!res.ok) throw new Error('Network error');
      setCardListData(await res.json());
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCardList(); }, []);

  const requestedData = useMemo(() =>
    cardListData.filter((r) => !['rejected', 'delivered'].includes(r.cstatus)), [cardListData]);
  const completedData = useMemo(() =>
    cardListData.filter((r) =>  ['rejected', 'delivered'].includes(r.cstatus)), [cardListData]);

  const handleStatusChange = async (cardId, status, e) => {
    if (!status) return;
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `Change card status to "${status}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: `Yes, ${status}!`,
    });
    if (result.isConfirmed) {
      try {
        const token = sessionStorage.getItem('token');
        const res = await fetch(`${baseurl}/admin/CardList/${cardId}`, {
          method: 'PUT',
          headers: { Authorization: `${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ status }),
        });
        if (!res.ok) throw new Error();
        Swal.fire('Updated!', `Status changed to ${status}.`, 'success');
        fetchCardList();
      } catch {
        Swal.fire('Error', 'Error while updating status.', 'error');
      }
    } else {
      e.target.value = '';
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: 300 }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="content container-fluid py-3">

      {/* Requested Card List */}
      <h3 className="text-center mb-3">Requested Card List</h3>
      <div className="card shadow-sm mb-5">
        <div className="card-body">
          <CardTable
            data={requestedData}
            isCompleted={false}
            roleid={roleid}
            onStatusChange={handleStatusChange}
            filename="RequestedCardList"
          />
        </div>
      </div>

      {/* Completed Card List */}
      <h3 className="text-center mb-2" style={{ color: '#374151' }}>
        Completed Card List{' '}
        <span className="badge bg-secondary ms-1" style={{ fontSize: '0.55em', verticalAlign: 'middle' }}>
          Rejected &amp; Delivered
        </span>
      </h3>
      <div className="card shadow-sm mb-5">
        <div className="card-body">
          <CardTable
            data={completedData}
            isCompleted={true}
            roleid={roleid}
            onStatusChange={handleStatusChange}
            filename="CompletedCardList"
          />
        </div>
      </div>

    </div>
  );
};

export default CardList;